/**
 * Lab Filter Bar
 * Filters for the analytics dashboard
 */
import { formatStatLabel } from '@parlay-picker/shared/statLabels';
import type { ReactElement } from 'react';
import type { AnalyticsScope, LabFilters, UserWithTrackedPicks } from '../hooks/useLabDashboard';

// =============================================================================
// Types
// =============================================================================

interface LabFilterBarProps {
  filters: LabFilters;
  setFilters: (filters: Partial<LabFilters>) => void;
  availableConfigs: string[];
  availablePropTypes: string[];
  availableUsers: UserWithTrackedPicks[];
  /** Whether to show pick-level filters (tier, direction) - hide in parlay mode */
  showPickFilters?: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const DATE_RANGE_OPTIONS = [
  { value: 'all', label: 'All Time' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '14d', label: 'Last 14 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '60d', label: 'Last 60 Days' },
];

const TIER_OPTIONS = [
  { value: '', label: 'All Tiers' },
  { value: 'Safe', label: 'Safe Play' },
  { value: 'Value', label: 'Value Play' },
  { value: 'Upside', label: 'Upside Play' },
];

const DIRECTION_OPTIONS = [
  { value: '', label: 'All Directions' },
  { value: 'Over', label: 'Over' },
  { value: 'Under', label: 'Under' },
];

const MIN_SAMPLE_OPTIONS = [
  { value: 1, label: 'Min 1 pick' },
  { value: 3, label: 'Min 3 picks' },
  { value: 5, label: 'Min 5 picks' },
  { value: 10, label: 'Min 10 picks' },
  { value: 20, label: 'Min 20 picks' },
];

// =============================================================================
// Component
// =============================================================================

export function LabFilterBar({
  filters,
  setFilters,
  availableConfigs,
  availablePropTypes,
  availableUsers,
  showPickFilters = true,
}: LabFilterBarProps): ReactElement {
  // Get scope description
  const getScopeDescription = (): string => {
    switch (filters.scope) {
      case 'all':
        return 'Analyzing all AI-generated picks (platform-wide)';
      case 'tracked':
        return 'Analyzing all tracked picks across all users';
      case 'user': {
        const selectedUser = availableUsers.find((u) => u.id === filters.userId);
        return selectedUser
          ? `Analyzing ${selectedUser.email}'s tracked picks`
          : 'Select a user to analyze their tracked picks';
      }
      default:
        return '';
    }
  };

  return (
    <div className="bg-theme-surface/50 rounded-lg p-4">
      {/* Scope Toggle - Prominent at top */}
      <div className="mb-4 pb-4 border-b border-theme-border">
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-xs text-theme-text-secondary">Analytics Scope:</span>
          <div className="flex rounded-lg overflow-hidden border border-theme-border">
            <button
              type="button"
              onClick={() => setFilters({ scope: 'all' as AnalyticsScope, userId: null })}
              className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                filters.scope === 'all'
                  ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                  : 'bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-surface-hover/80'
              }`}
            >
              All AI Picks
            </button>
            <button
              type="button"
              onClick={() => setFilters({ scope: 'tracked' as AnalyticsScope, userId: null })}
              className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                filters.scope === 'tracked'
                  ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                  : 'bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-surface-hover/80'
              }`}
            >
              All Tracked
            </button>
            <button
              type="button"
              onClick={() => setFilters({ scope: 'user' as AnalyticsScope })}
              className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                filters.scope === 'user'
                  ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                  : 'bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-surface-hover/80'
              }`}
            >
              By User
            </button>
          </div>

          {/* User selector - only visible when scope is 'user' */}
          {filters.scope === 'user' &&
            (availableUsers.length > 0 ? (
              <select
                value={filters.userId ?? ''}
                onChange={(e) => setFilters({ userId: e.target.value || null })}
                className="bg-theme-surface-hover text-theme-text text-sm rounded px-3 py-1.5 border border-theme-border focus:border-theme-primary focus:outline-none"
              >
                <option value="">Select User...</option>
                {availableUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.email} ({user.trackedCount} picks)
                  </option>
                ))}
              </select>
            ) : (
              <span className="text-xs text-theme-warning">No users with tracked picks</span>
            ))}

          <span className="text-xs text-theme-text-tertiary">{getScopeDescription()}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        {/* Date Range */}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-theme-text-secondary">Date Range</label>
          <select
            value={filters.dateRange}
            onChange={(e) => setFilters({ dateRange: e.target.value })}
            className="bg-theme-surface-hover text-theme-text text-sm rounded px-3 py-1.5 border border-theme-border focus:border-theme-primary focus:outline-none"
          >
            {DATE_RANGE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Config */}
        {availableConfigs.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs text-theme-text-secondary">Config</label>
            <select
              value={filters.config}
              onChange={(e) => setFilters({ config: e.target.value })}
              className="bg-theme-surface-hover text-theme-text text-sm rounded px-3 py-1.5 border border-theme-border focus:border-theme-primary focus:outline-none"
            >
              <option value="">All Configs</option>
              {availableConfigs.map((config) => (
                <option key={config} value={config}>
                  {config}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Tier - only for picks mode */}
        {showPickFilters && (
          <div className="flex flex-col gap-1">
            <label className="text-xs text-theme-text-secondary">Tier</label>
            <select
              value={filters.tier}
              onChange={(e) => setFilters({ tier: e.target.value })}
              className="bg-theme-surface-hover text-theme-text text-sm rounded px-3 py-1.5 border border-theme-border focus:border-theme-primary focus:outline-none"
            >
              {TIER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Prop Type - only for picks mode */}
        {showPickFilters && availablePropTypes.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs text-theme-text-secondary">Prop Type</label>
            <select
              value={filters.propType}
              onChange={(e) => setFilters({ propType: e.target.value })}
              className="bg-theme-surface-hover text-theme-text text-sm rounded px-3 py-1.5 border border-theme-border focus:border-theme-primary focus:outline-none"
            >
              <option value="">All Props</option>
              {availablePropTypes.map((prop) => (
                <option key={prop} value={prop}>
                  {formatStatLabel(prop)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Direction - only for picks mode */}
        {showPickFilters && (
          <div className="flex flex-col gap-1">
            <label className="text-xs text-theme-text-secondary">Direction</label>
            <select
              value={filters.direction}
              onChange={(e) => setFilters({ direction: e.target.value })}
              className="bg-theme-surface-hover text-theme-text text-sm rounded px-3 py-1.5 border border-theme-border focus:border-theme-primary focus:outline-none"
            >
              {DIRECTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Min Sample Size - only for picks mode */}
        {showPickFilters && (
          <div className="flex flex-col gap-1">
            <label className="text-xs text-theme-text-secondary">Sample Size</label>
            <select
              value={filters.minSampleSize}
              onChange={(e) => setFilters({ minSampleSize: parseInt(e.target.value, 10) })}
              className="bg-theme-surface-hover text-theme-text text-sm rounded px-3 py-1.5 border border-theme-border focus:border-theme-primary focus:outline-none"
            >
              {MIN_SAMPLE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
